/** @flow */

import React, { useEffect, useRef, useState } from "react";
import GoogleChart from "react-google-charts";
import html2canvas from "html2canvas";
import { localize } from "redux-i18n";
import classNames from "classnames";
import type { ChartWrapperOptions } from "react-google-charts";
import { View } from "@components";

import { debounce } from "@utils/time";

import "./style.scss";

export const chartDefaultColors = [
  "#FF871A", // coffee-d1
  "#004F9F", // ice-d3
  "#3EC9F7", // ice-d1
  "#5BCB42", // mint-d1
  "#9E51B2", // berry-d1
  "#5C8068", // mint-d2
  "#786780", // berry-d2
  "#000000",
];

export type Props = $Exact<{
  options?: Object,
  title: string,
  legendToggle?: boolean,
  chartWidth?: number,
  chartHeight?: number, // value in pixels
  type: "LineChart" | "PieChart" | "ColumnChart" | "BarChart",
  values: Array<Array<string | number | Object>>,
  ...PropsFromLocalize,
}>;

export default function Chart(props: Props) {
  const [chartRef, setChartRef] = useState();
  const [chartSize, setChartSize] = useState({
    width: props.chartWidth || "100%",
    height: props.chartHeight || "100%",
  });

  const defaultOptions = {
    BarChart: {
      legend: {
        position: "top",
        alignment: "center",
      },
      seriesType: "bars",
    },
    ColumnChart: {
      legend: {
        position: "top",
        alignment: "center",
      },
      seriesType: "bars",
    },
    PieChart: {
      pieHole: 0.3,
      legend: {},

      maximize: true,
    },
    LineChart: {
      legend: {
        position: "top",
        alignment: "center",
        maxLines: 3,
        textStyle: {
          fontSize: 14,
        },
      },
      hAxis: { showTextEvery: 1 }, // How many horizontal axis labels to show, where 1 means show every label, 2 means show every other label, and so on. Default is to try to show as many labels as possible without overlapping.
    },
    default: {
      colors: chartDefaultColors,
      animation: {
        duration: 250,
        easing: "out",
        startup: false,
      },
      chartArea: { height: "70%", width: "90%" },
      annotations: {
        alwaysOutside: true,
        highContrast: false,
      },
    },
  };

  const chartWrapper = chartRef?.parentNode?.parentNode;

  useEffect(() => {
    chartWrapper && resizeObserver.observe(chartWrapper);
  }, [chartWrapper]);

  const resizeObserver = new ResizeObserver(entries => {
    for (let entry of entries) {
      debounce(() => resizeChart(entry), entry.target, 10);
    }
  });

  const chartOptions = {
    ...defaultOptions.default,
    ...defaultOptions[props.type],
    width: chartSize.width,
    height: chartSize.height,
    ...props.options,
  };
  function resizeChart(chart) {
    const width = chart.target.clientWidth;
    const height = chart.target.clientHeight;
    setChartSize({
      width: width, // We take chart's wrapper width
      height: height, // Should be the same aspect as .chart-graphic
    });
  }
  const handleChartExport = async () => {
    // TODO : This should be refactored and remove the external lib
    const canvas = await html2canvas(chartRef, {
      scrollX: 0,
      scrollY: -window.scrollY, // so we take page scroll into account
    });
    const data = canvas.toDataURL("image/jpg");
    const link = document.createElement("a");
    link.href = data;
    link.download = `${props.title}.jpg` || "chart-image.jpg";
    document.body.appendChild(link);
    // we need this to prevent event from bubbling up
    const event = new MouseEvent("click", {
      view: window,
      bubbles: false,
      cancelable: true,
    });
    link.dispatchEvent(event);
    document.body.removeChild(link);
  };

  return (
    <View className="chart-wrapper">
      <View direction="row" justifyContent="space-between" className="chart-title">
        <h6>{props.title || ""}</h6>

        {props.values.length > 1 && (
          <span
            className="material-icons pointer"
            data-html2canvas-ignore="true"
            onClick={handleChartExport}
          >
            download
          </span>
        )}
      </View>
      <View className="chart-graphic">
        {props.values.length > 1 ? (
          <GoogleChart
            chartType={props.type || "ColumnChart"}
            data={props.values}
            getChartWrapper={chartRef => {
              setChartRef(chartRef.getContainer());
            }}
            options={chartOptions}
            legendToggle={props.legendToggle}
          />
        ) : (
          <View flex="1" alignItems="center" justifyContent="center">
            {"No data"}
          </View>
        )}
      </View>
    </View>
  );
}
